import React from "react";
import axios from "axios";
import App from "./App";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { msalConfig } from "./configurations/entraId.azure.config";
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from "@azure/msal-browser";
import store from "./store";
import "./css/index.css";
import { WEB_AUTH_URL, WEB_CORE_URL } from "./configurations/paths.config";
import { removeAuth } from "./common/Helpers";

initializeIcons();

let isRefreshing = false;
let failedQueue = [];

const SKIP_REFRESH_URLS = [
  '/apie/refreshToken',
  '/api/login',
  '/api/do-login',
  '/api/do-login-aad',
  '/api/loginAAD',
  '/api/2flogin',
  '/api/account/validate-email-login',
  '/api/account/validate-email-login-aad',
  '/api/account/regenerate-code',
  '/api/account/get-domains',
  '/api/account/get-domains-fpw',
  '/api/account/confirm-email',
  '/api/resetpassword',
];

const processQueue = (error) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    }
    else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

const getRequestUrl = (config) => {
  return config && config.url ? config.url : "";
};

const shouldSkipRefresh = (config) => {
  const url = getRequestUrl(config);

  if (config && config.__skipRefresh === true) {
    return true;
  }

  return SKIP_REFRESH_URLS.some((skipUrl) => url.indexOf(skipUrl) !== -1);
};

const redirectToLogout = async () => {
  try {
    await removeAuth();
  }
  catch (error) {
    // Si limpiar la sesión local falla, de todas formas se redirige a logout.
  }

  window.location.href = `${WEB_CORE_URL}/logout?stats=success`;
};

axios.interceptors.response.use(
  (response) => {
    const code = response && response.data ? response.data.code : null;

    if (code === 'SESSION_REMOVED') {
      redirectToLogout();
      return Promise.reject(new Error('SESSION_REMOVED'));
    }

    // SESSION_RECREATED no debe recargar la página. El refresh debe ser transparente.
    return response;
  },
  async (error) => {
    const originalRequest = error && error.config ? error.config : null;
    const response = error && error.response ? error.response : null;

    if (!response || !originalRequest) {
      return Promise.reject(error);
    }

    const status = response.status;
    const responseData = response.data;
    const code = responseData && responseData.code ? responseData.code : null;

    if (
      status === 401 &&
      code !== 'SESSION_CREATED_ERROR' &&
      originalRequest._retry !== true &&
      shouldSkipRefresh(originalRequest) === false
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => axios(originalRequest))
          .catch((queueError) => Promise.reject(queueError));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post('/apie/refreshToken', null, { __skipRefresh: true });
        processQueue(null);
        return axios(originalRequest);
      }
      catch (refreshError) {
        processQueue(refreshError);
        await redirectToLogout();
        return Promise.reject(refreshError);
      }
      finally {
        isRefreshing = false;
      }
    }

    if (status === 400 && responseData === 'MdLw-Unauthorized') {
      try {
        await axios({
          method: "GET",
          url: `${WEB_AUTH_URL}/remove-cookies-ext`,
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
          __skipRefresh: true,
        });
      }
      catch (removeCookiesError) {
        // Si el endpoint externo falla, igual limpiamos local y redirigimos.
      }

      await redirectToLogout();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

const msalInstance = new PublicClientApplication(msalConfig);

ReactDOM.render(
  <Provider store={store}>
    <MsalProvider instance={msalInstance}>
      <App />
    </MsalProvider>
  </Provider>,
  document.getElementById("root")
);
